import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { getLegacyClaimants, LegacyClaimant, LegacyClaimantsPage } from '../api/legacy';
import { useAuth } from '../context/AuthContext';
import './LegacyClaimantsTable.css';

interface LegacyClaimantsTableProps {
  onClaimantClick?: (claimant: LegacyClaimant) => void;
}

export interface LegacyClaimantsTableRef {
  refresh: () => Promise<void>;
}

const LegacyClaimantsTable = forwardRef<LegacyClaimantsTableRef, LegacyClaimantsTableProps>(
  ({ onClaimantClick }, ref) => {
  const [claimants, setClaimants] = useState<LegacyClaimant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { isAuthenticated } = useAuth();
  
  // Add render tracking
  console.log(`[RENDER] Component rendering - currentPage: ${currentPage}, claimants.length: ${claimants.length}, firstClaimantId: ${claimants[0]?.id || 'none'}`);

  const pageSize = 100; // Updated to show 100 per page

  // Fetch claimants function - NO dependencies on currentPage or searchTerm to avoid circular deps
  const fetchClaimants = useCallback(async (page: number, search: string) => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`[FETCH] Fetching page ${page} with pageSize ${pageSize}, search: "${search}"`);
      const data: LegacyClaimantsPage = await getLegacyClaimants(page, pageSize, search);
      console.log(`[FETCH] API Response - items: ${data.items.length}, total: ${data.total}, page: ${data.page}`);
      console.log(`[FETCH] First item ID (if any): ${data.items[0]?.id || 'No items'}`);
      
      console.log(`[FETCH] Setting claimants array with ${data.items.length} items`);
      setClaimants(data.items);
      console.log(`[FETCH] Setting totalRecords to ${data.total}`);
      setTotalRecords(data.total);
      const calculatedTotalPages = Math.ceil(data.total / pageSize);
      console.log(`[FETCH] Setting totalPages to ${calculatedTotalPages} (${data.total} / ${pageSize})`);
      setTotalPages(calculatedTotalPages);
      console.log(`[FETCH] State updates completed for page ${page}`);
    } catch (err: any) {
      console.error('Failed to fetch legacy claimants:', err);
      setError('Failed to load legacy claimants');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, pageSize]);

  // Fetch when page or search changes
  useEffect(() => {
    console.log(`[EFFECT] useEffect triggered`);
    console.log(`[EFFECT] isAuthenticated: ${isAuthenticated}, currentPage: ${currentPage}, searchTerm: "${searchTerm}"`);
    if (isAuthenticated) {
      console.log(`[EFFECT] Calling fetchClaimants for page ${currentPage} with search "${searchTerm}"`);
      fetchClaimants(currentPage, searchTerm);
    } else {
      console.log(`[EFFECT] Not authenticated, skipping fetch`);
    }
  }, [isAuthenticated, currentPage, searchTerm, fetchClaimants]);

  // Expose refresh function via ref
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await fetchClaimants(currentPage, searchTerm);
    }
  }), [fetchClaimants, currentPage, searchTerm]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    console.log(`[PAGINATION] handlePageChange called with page: ${page}`);
    console.log(`[PAGINATION] Current page: ${currentPage}, Total pages: ${totalPages}`);
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      console.log(`[PAGINATION] Setting current page to: ${page}`);
      setCurrentPage(page);
    } else {
      console.log(`[PAGINATION] Page change blocked - Invalid page number or same as current`);
    }
  };

  // Format reference
  const formatReference = (claimant: LegacyClaimant) => {
    return claimant.legacyId || claimant.id.slice(0, 8);
  };

  // Format name
  const formatName = (claimant: LegacyClaimant) => {
    return `${claimant.firstName || ''} ${claimant.lastName || ''}`.trim() || 'N/A';
  };

  // Get status display
  const getStatusDisplay = (claimant: LegacyClaimant) => {
    if (claimant.audited) {
      return 'Audited';
    }
    return 'Pending Audit';
  };

  // Get status class
  const getStatusClass = (claimant: LegacyClaimant) => {
    if (claimant.audited) {
      return 'status-audited';
    }
    return 'status-pending';
  };

  // Get audit status badge
  const getAuditStatusBadge = (claimant: LegacyClaimant) => {
    if (claimant.audited) {
      return (
        <span className="audit-badge audit-completed">
          Audited
        </span>
      );
    }
    return (
      <span className="audit-badge audit-pending">
        Pending
      </span>
    );
  };

  if (!isAuthenticated) {
    return <div>Please log in to view legacy claimants.</div>;
  }

  if (loading && claimants.length === 0) {
    console.log('[RENDER] Showing loading spinner');
    return (
      <div className="legacy-claimants-loading">
        <div className="loading-spinner"></div>
        <p>Loading legacy claimants...</p>
      </div>
    );
  }

  return (
    <div className="legacy-claimants-container">
      {/* Search Bar */}
      <div className="search-controls">
        <form className="search-form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search by name, email, or reference..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
        <button 
          className="refresh-button"
          onClick={() => fetchClaimants(currentPage, searchTerm)}
          disabled={loading}
          title="Refresh table"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Desktop Table */}
      <div className="desktop-table">
        <table>
          <thead>
            <tr>
              <th>Reference</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Postcode</th>
              <th>Status</th>
              <th>Claims</th>
              <th>Audit Status</th>
            </tr>
          </thead>
          <tbody>
            {claimants.map((claimant) => (
              <tr
                key={claimant.id}
                onClick={() => onClaimantClick?.(claimant)}
                className="clickable-row"
              >
                <td>{formatReference(claimant)}</td>
                <td>{formatName(claimant)}</td>
                <td>{claimant.email || 'N/A'}</td>
                <td>{claimant.phoneNumber || 'N/A'}</td>
                <td>{claimant.postcode || 'N/A'}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(claimant)}`}>
                    {getStatusDisplay(claimant)}
                  </span>
                </td>
                <td>{claimant.claimsCount || 0}</td>
                <td>{getAuditStatusBadge(claimant)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-claimants-list">
        {claimants.map((claimant) => (
          <div
            key={claimant.id}
            className="mobile-claimant-card"
            onClick={() => onClaimantClick?.(claimant)}
          >
            <div className="mobile-card-header">
              <span className="mobile-card-reference">{formatReference(claimant)}</span>
              <span className={`mobile-card-status ${getStatusClass(claimant)}`}>
                {getStatusDisplay(claimant)}
              </span>
            </div>
            <div className="mobile-card-content">
              <div className="mobile-card-row">
                <span className="mobile-card-label">Name:</span>
                <span className="mobile-card-value">{formatName(claimant)}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Email:</span>
                <span className="mobile-card-value">{claimant.email || 'N/A'}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Phone:</span>
                <span className="mobile-card-value">{claimant.phoneNumber || 'N/A'}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Postcode:</span>
                <span className="mobile-card-value">{claimant.postcode || 'N/A'}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Claims:</span>
                <span className="mobile-card-value">{claimant.claimsCount || 0}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Audit:</span>
                <span className="mobile-card-value">{getAuditStatusBadge(claimant)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => {
              console.log('[BUTTON] First page button clicked');
              handlePageChange(1);
            }}
            disabled={currentPage === 1}
            className="pagination-button pagination-first"
            title="First page"
          >
            ««
          </button>
          <button
            onClick={() => {
              console.log('[BUTTON] Previous button clicked');
              handlePageChange(currentPage - 1);
            }}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          
          {/* Page numbers */}
          <div className="pagination-numbers">
            {(() => {
              const pages = [];
              let startPage = Math.max(1, currentPage - 2);
              let endPage = Math.min(totalPages, currentPage + 2);
              
              // Always show first page
              if (startPage > 1) {
                pages.push(
                  <button
                    key={1}
                    onClick={() => {
                      console.log('[BUTTON] Page number 1 clicked');
                      handlePageChange(1);
                    }}
                    className="pagination-number"
                  >
                    1
                  </button>
                );
                if (startPage > 2) {
                  pages.push(<span key="dots1" className="pagination-dots">...</span>);
                }
              }
              
              // Show page numbers around current page
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => {
                      console.log(`[BUTTON] Page number ${i} clicked`);
                      handlePageChange(i);
                    }}
                    className={`pagination-number ${i === currentPage ? 'active' : ''}`}
                    disabled={i === currentPage}
                  >
                    {i}
                  </button>
                );
              }
              
              // Always show last page
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(<span key="dots2" className="pagination-dots">...</span>);
                }
                pages.push(
                  <button
                    key={totalPages}
                    onClick={() => {
                      console.log(`[BUTTON] Page number ${totalPages} (last) clicked`);
                      handlePageChange(totalPages);
                    }}
                    className="pagination-number"
                  >
                    {totalPages}
                  </button>
                );
              }
              
              return pages;
            })()}
          </div>
          
          <button
            onClick={() => {
              console.log('[BUTTON] Next button clicked');
              handlePageChange(currentPage + 1);
            }}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
          <button
            onClick={() => {
              console.log('[BUTTON] Last page button clicked');
              handlePageChange(totalPages);
            }}
            disabled={currentPage === totalPages}
            className="pagination-button pagination-last"
            title="Last page"
          >
            »»
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages} | {totalRecords > 0 ? `Showing ${((currentPage - 1) * pageSize) + 1}-${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords} records` : 'No records'}
          </span>
        </div>
      )}

      {/* No results message */}
      {!loading && claimants.length === 0 && (
        <div className="no-results">
          <p>No legacy claimants found.</p>
        </div>
      )}
    </div>
  );
});

LegacyClaimantsTable.displayName = 'LegacyClaimantsTable';

export default LegacyClaimantsTable;